import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import InputField from 'components/fields/InputField';
import { toast } from 'react-toastify';
import { MdSave, MdClose, MdExpandMore, MdExpandLess, MdKeyboardArrowDown } from 'react-icons/md';
import GoBack from 'components/actions/GoBack';
import { frenchAreas } from 'variables/industries';
import { useSavedRegions } from 'services/savedRegionsService';
import { useIndustries } from 'services/industryService';
import Select from 'react-select';

interface FormTemplate {
    name: string;
    isUniversal?: boolean;
    industry?: string;
    subIndustry?: string;
    fieldOrder?: string[];
    regions?: string[];
    minimumPrice?: number;
    borderColor: string;
    borderWidth: string;
    borderRadius: string;
    backgroundColor: string;
    textColor: string;
    labelColor: string;
    buttonBackgroundColor: string;
    buttonTextColor: string;
    inputBackgroundColor: string;
    inputBorderColor: string;
    inputTextColor: string;
    fontFamily?: string;
    fontSize?: string;
    ctaText?: string;
    isDefault: boolean;
}

interface DesignPreset {
    id: string;
    name: string;
    description: string;
    preview: { bg: string; accent: string; text: string };
    style: Partial<FormTemplate>;
}

interface FormBuilderField {
    id: string;
    name: string;
    type: 'string' | 'select' | 'multi-select' | 'number';
    question?: string;
    options?: string[];
    required?: boolean;
}

const FIELD_PLACEHOLDERS: Record<string, string> = {
    'Prénom': 'Jean',
    'Nom': 'Dupont',
    'Téléphone': '06 12 34 56 78',
    'Email': 'jean@exemple.com',
    'Code postal': '75001',
    'Département': 'Paris (75)',
    'Adresse': '12 rue de la Paix',
    'Ville': 'Paris',
    'Message': 'Votre message...',
    'Surface': '120 m²',
    'Budget': '250 000 €',
    'Type de bien': 'Appartement',
    'Projet': 'Achat résidence principale',
    'Situation professionnelle': 'Salarié',
    'Revenus': '3 500 € / mois',
    'Apport': '50 000 €',
    'Date de naissance': '01/01/1985',
    'Nombre de personnes': '3',
    'Type de contrat': 'CDI',
};


// react-select ships unstyled by default (blue focus ring, square corners) — align it with
// the leadrs design tokens (leadrs-stroke / leadrs-brand / leadrs-purple, rounded-xl, Geist).
const regionsSelectStyles = {
    control: (base: any, state: any) => ({
        ...base,
        minHeight: 48,
        borderRadius: 12,
        borderColor: state.isFocused ? '#1C0F53' : '#F3EDE4',
        boxShadow: 'none',
        fontFamily: 'Geist, sans-serif',
        fontSize: 14,
        '&:hover': { borderColor: '#1C0F53' },
    }),
    placeholder: (base: any) => ({ ...base, color: '#676767' }),
    multiValue: (base: any) => ({ ...base, backgroundColor: '#EAE6FF', borderRadius: 9999, paddingLeft: 4 }),
    multiValueLabel: (base: any) => ({
        ...base,
        color: '#1C0F53',
        fontFamily: 'Geist, sans-serif',
        fontSize: 12,
        fontWeight: 600,
    }),
    multiValueRemove: (base: any) => ({
        ...base,
        color: '#1C0F53',
        borderRadius: 9999,
        '&:hover': { backgroundColor: '#1C0F53', color: '#ffffff' },
    }),
    menu: (base: any) => ({
        ...base,
        borderRadius: 12,
        border: '1px solid #F3EDE4',
        boxShadow: '0 2px 8px rgba(20,20,18,0.08)',
        overflow: 'hidden',
    }),
    option: (base: any, state: any) => ({
        ...base,
        fontFamily: 'Geist, sans-serif',
        fontSize: 14,
        backgroundColor: state.isSelected ? '#1C0F53' : state.isFocused ? '#EAE6FF' : '#ffffff',
        color: state.isSelected ? '#ffffff' : '#141412',
        cursor: 'pointer',
    }),
    dropdownIndicator: (base: any) => ({ ...base, color: '#676767', '&:hover': { color: '#1C0F53' } }),
    clearIndicator: (base: any) => ({ ...base, color: '#676767', '&:hover': { color: '#D63C3C' } }),
    indicatorSeparator: (base: any) => ({ ...base, backgroundColor: '#F3EDE4' }),
};

const CHECKERBOARD: React.CSSProperties = {
    backgroundImage: 'linear-gradient(45deg, #d0d0d0 25%, transparent 25%), linear-gradient(-45deg, #d0d0d0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #d0d0d0 75%), linear-gradient(-45deg, transparent 75%, #d0d0d0 75%)',
    backgroundSize: '8px 8px',
    backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
    backgroundColor: '#f5f5f5',
};

const DESIGN_PRESETS: DesignPreset[] = [
    {
        id: 'transparent',
        name: 'Transparent',
        description: 'S\'intègre à votre site',
        preview: { bg: 'transparent', accent: '#4f46e5', text: '#1f2937' },
        style: {
            backgroundColor: 'transparent', borderColor: 'transparent', borderWidth: '0px', borderRadius: '8px',
            textColor: '#1f2937', labelColor: '#374151', buttonBackgroundColor: '#4f46e5',
            buttonTextColor: '#ffffff', inputBackgroundColor: 'rgba(255,255,255,0.9)', inputBorderColor: '#e5e7eb',
            inputTextColor: '#1f2937', fontFamily: 'inherit', fontSize: '16px',
        },
    },
    {
        id: 'classique',
        name: 'Classique',
        description: 'Propre et professionnel',
        preview: { bg: '#ffffff', accent: '#4f46e5', text: '#1f2937' },
        style: {
            backgroundColor: '#ffffff', borderColor: '#e5e7eb', borderWidth: '1px', borderRadius: '12px',
            textColor: '#1f2937', labelColor: '#374151', buttonBackgroundColor: '#4f46e5',
            buttonTextColor: '#ffffff', inputBackgroundColor: '#ffffff', inputBorderColor: '#d1d5db',
            inputTextColor: '#1f2937', fontFamily: 'inherit', fontSize: '16px',
        },
    },
    {
        id: 'sombre',
        name: 'Sombre',
        description: 'Moderne et élégant',
        preview: { bg: '#0f172a', accent: '#818cf8', text: '#f1f5f9' },
        style: {
            backgroundColor: '#0f172a', borderColor: '#1e293b', borderWidth: '1px', borderRadius: '16px',
            textColor: '#f1f5f9', labelColor: '#94a3b8', buttonBackgroundColor: '#818cf8',
            buttonTextColor: '#ffffff', inputBackgroundColor: '#1e293b', inputBorderColor: '#334155',
            inputTextColor: '#f1f5f9', fontFamily: 'inherit', fontSize: '16px',
        },
    },
    {
        id: 'minimaliste',
        name: 'Minimaliste',
        description: 'Épuré et sobre',
        preview: { bg: '#fafafa', accent: '#18181b', text: '#18181b' },
        style: {
            backgroundColor: '#fafafa', borderColor: 'transparent', borderWidth: '0px', borderRadius: '0px',
            textColor: '#18181b', labelColor: '#52525b', buttonBackgroundColor: '#18181b',
            buttonTextColor: '#ffffff', inputBackgroundColor: '#ffffff', inputBorderColor: '#e4e4e7',
            inputTextColor: '#18181b', fontFamily: 'inherit', fontSize: '16px',
        },
    },
    {
        id: 'soleil',
        name: 'Soleil',
        description: 'Chaleureux et accueillant',
        preview: { bg: '#fffbeb', accent: '#d97706', text: '#92400e' },
        style: {
            backgroundColor: '#fffbeb', borderColor: '#fde68a', borderWidth: '1px', borderRadius: '16px',
            textColor: '#92400e', labelColor: '#b45309', buttonBackgroundColor: '#d97706',
            buttonTextColor: '#ffffff', inputBackgroundColor: '#ffffff', inputBorderColor: '#fde68a',
            inputTextColor: '#78350f', fontFamily: 'inherit', fontSize: '16px',
        },
    },
    {
        id: 'nature',
        name: 'Nature',
        description: 'Frais et naturel',
        preview: { bg: '#f0fdf4', accent: '#16a34a', text: '#14532d' },
        style: {
            backgroundColor: '#f0fdf4', borderColor: '#bbf7d0', borderWidth: '1px', borderRadius: '14px',
            textColor: '#14532d', labelColor: '#166534', buttonBackgroundColor: '#16a34a',
            buttonTextColor: '#ffffff', inputBackgroundColor: '#ffffff', inputBorderColor: '#bbf7d0',
            inputTextColor: '#14532d', fontFamily: 'inherit', fontSize: '16px',
        },
    },
    {
        id: 'luxe',
        name: 'Luxe',
        description: 'Premium et raffiné',
        preview: { bg: '#1c1917', accent: '#ca8a04', text: '#fef3c7' },
        style: {
            backgroundColor: '#1c1917', borderColor: '#ca8a04', borderWidth: '1px', borderRadius: '8px',
            textColor: '#fef3c7', labelColor: '#d4b96a', buttonBackgroundColor: '#ca8a04',
            buttonTextColor: '#1c1917', inputBackgroundColor: '#292524', inputBorderColor: '#44403c',
            inputTextColor: '#fef3c7', fontFamily: 'Georgia, serif', fontSize: '16px',
        },
    },
    {
        id: 'azur',
        name: 'Azur',
        description: 'Frais et digital',
        preview: { bg: '#eff6ff', accent: '#2563eb', text: '#1e3a8a' },
        style: {
            backgroundColor: '#eff6ff', borderColor: '#bfdbfe', borderWidth: '1px', borderRadius: '16px',
            textColor: '#1e3a8a', labelColor: '#1d4ed8', buttonBackgroundColor: '#2563eb',
            buttonTextColor: '#ffffff', inputBackgroundColor: '#ffffff', inputBorderColor: '#bfdbfe',
            inputTextColor: '#1e3a8a', fontFamily: 'inherit', fontSize: '16px',
        },
    },
    {
        id: 'rose',
        name: 'Éclat Rose',
        description: 'Doux et moderne',
        preview: { bg: '#fdf2f8', accent: '#db2777', text: '#831843' },
        style: {
            backgroundColor: '#fdf2f8', borderColor: '#fbcfe8', borderWidth: '1px', borderRadius: '20px',
            textColor: '#831843', labelColor: '#9d174d', buttonBackgroundColor: '#db2777',
            buttonTextColor: '#ffffff', inputBackgroundColor: '#ffffff', inputBorderColor: '#fbcfe8',
            inputTextColor: '#831843', fontFamily: 'inherit', fontSize: '16px',
        },
    },
    {
        id: 'carre-blanc',
        name: 'Carré Net',
        description: 'Angles nets, sans arrondi',
        preview: { bg: '#ffffff', accent: '#111827', text: '#111827' },
        style: {
            backgroundColor: '#ffffff', borderColor: '#111827', borderWidth: '2px', borderRadius: '0px',
            textColor: '#111827', labelColor: '#374151', buttonBackgroundColor: '#111827',
            buttonTextColor: '#ffffff', inputBackgroundColor: '#ffffff', inputBorderColor: '#111827',
            inputTextColor: '#111827', fontFamily: 'inherit', fontSize: '16px',
        },
    },
    {
        id: 'carre-ardoise',
        name: 'Ardoise',
        description: 'Sombre et carré, editorial',
        preview: { bg: '#1e293b', accent: '#38bdf8', text: '#f8fafc' },
        style: {
            backgroundColor: '#1e293b', borderColor: '#38bdf8', borderWidth: '2px', borderRadius: '0px',
            textColor: '#f8fafc', labelColor: '#94a3b8', buttonBackgroundColor: '#38bdf8',
            buttonTextColor: '#0f172a', inputBackgroundColor: '#0f172a', inputBorderColor: '#334155',
            inputTextColor: '#f8fafc', fontFamily: 'inherit', fontSize: '16px',
        },
    },
    {
        id: 'carre-kraft',
        name: 'Kraft',
        description: 'Carré et chaleureux',
        preview: { bg: '#fef9f0', accent: '#92400e', text: '#451a03' },
        style: {
            backgroundColor: '#fef9f0', borderColor: '#92400e', borderWidth: '2px', borderRadius: '0px',
            textColor: '#451a03', labelColor: '#78350f', buttonBackgroundColor: '#92400e',
            buttonTextColor: '#fef9f0', inputBackgroundColor: '#ffffff', inputBorderColor: '#92400e',
            inputTextColor: '#451a03', fontFamily: 'Georgia, serif', fontSize: '16px',
        },
    },
];

const defaultTemplate: FormTemplate = {
    name: 'Nouvelle intégration API',
    industry: '',
    subIndustry: '',
    regions: [],
    minimumPrice: undefined,
    borderColor: '#e5e7eb',
    borderWidth: '1px',
    borderRadius: '12px',
    backgroundColor: '#ffffff',
    textColor: '#1f2937',
    labelColor: '#374151',
    buttonBackgroundColor: '#4f46e5',
    buttonTextColor: '#ffffff',
    inputBackgroundColor: '#ffffff',
    inputBorderColor: '#d1d5db',
    inputTextColor: '#1f2937',
    fontFamily: 'inherit',
    fontSize: '16px',
    ctaText: 'Envoyer ma demande',
    isDefault: false,
};

const NewFormTemplate = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isUniversal = searchParams.get('universal') === '1';
    const { industries: industriesData, loading: industriesLoading } = useIndustries();
    const [template, setTemplate] = useState<FormTemplate>(() => (
        isUniversal ? { ...defaultTemplate, name: 'Iframe' } : defaultTemplate
    ));
    const [isLoading, setIsLoading] = useState(false);
    const [formFields, setFormFields] = useState<FormBuilderField[]>([]);
    const { savedRegions, saveRegions } = useSavedRegions();
    const [isCampaignExpanded, setIsCampaignExpanded] = useState(true);
    const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
    const [designTab, setDesignTab] = useState<'container' | 'text' | 'inputs' | 'button' | 'order'>('container');

    useEffect(() => {
        if (savedRegions.length > 0 && (template.regions || []).length === 0) {
            handleMultiChange({ regions: savedRegions });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [savedRegions]);

    useEffect(() => {
        axios.get(`${CONFIG.apiUrl}/form-field`)
            .then((res) => setFormFields(res.data.results || []))
            .catch(() => {});
    }, []);

    const handleCreate = async () => {
        // Universal templates have no per-industry identity to name around, so unlike product
        // templates the name isn't required — an empty one just falls back to a sensible default.
        if (!template.name.trim() && !isUniversal) {
            toast.error("Le nom de l'intégration API est requis");
            return;
        }

        setIsLoading(true);
        try {
            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: 'Bearer ' + localStorage.getItem('token'),
                },
            };
            // Universal templates have no fixed industry — it's resolved dynamically per page
            // from the Pixel's classification, and pricing comes from the seller's per-industry
            // price grid instead of a single template-level price.
            const payload = isUniversal
                ? { ...template, name: template.name.trim() || 'Iframe', isUniversal: true, industry: undefined, subIndustry: undefined, regions: undefined, minimumPrice: undefined, fieldOrder: undefined }
                : template;
            await axios.post(`${CONFIG.apiUrl}/form-template`, payload, config);
            toast.success('Intégration API créée avec succès');
            navigate('/integrations');
        } catch (error) {
            console.error('Error creating template:', error);
            toast.error("Erreur lors de la création de l'intégration API");
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (field: keyof FormTemplate, value: any) => {
        setTemplate({ ...template, [field]: value });
    };

    const handleMultiChange = (updates: Partial<FormTemplate>) => {
        setTemplate({ ...template, ...updates });
    };

    const applyPreset = (preset: DesignPreset) => {
        setSelectedPreset(preset.id);
        setTemplate((prev) => ({ ...prev, ...preset.style }));
    };

    const moveFieldInOrder = (fieldId: string, direction: 'up' | 'down') => {
        const industry = industriesData.find(i => i.name === template.industry);
        const mandatory = industry?.mandatoryFields || [];
        const order = template.fieldOrder || [];
        const visible = [...order, ...mandatory.filter(f => !order.includes(f))];
        const idx = visible.indexOf(fieldId);
        if (idx === -1) return;
        const target = direction === 'up' ? idx - 1 : idx + 1;
        if (target < 0 || target >= visible.length) return;
        const next = [...visible];
        [next[idx], next[target]] = [next[target], next[idx]];
        handleChange('fieldOrder', next);
    };

    const toggleOptionalField = (fieldId: string) => {
        const order = template.fieldOrder || [];
        handleChange('fieldOrder', order.includes(fieldId)
            ? order.filter(f => f !== fieldId)
            : [...order, fieldId]);
    };

    const handleIndustryChange = (value: string) => {
        const updates: Partial<FormTemplate> = { industry: value };
        const selectedIndustry = industriesData.find(i => i.name === value);
        if (value && selectedIndustry && selectedIndustry.subIndustries.length > 0) {
            updates.subIndustry = selectedIndustry.subIndustries[0].name;
        } else {
            updates.subIndustry = '';
        }
        handleMultiChange(updates);
    };

    const renderColorInput = (label: string, value: string, onChange: (value: string) => void) => (
        <div className="flex flex-col gap-2">
            <label className="font-geist text-sm font-medium text-leadrs-text">{label}</label>
            <div className="flex items-center gap-3">
                <input
                    type="color"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="h-10 w-20 cursor-pointer rounded-lg border border-leadrs-stroke"
                />
                <input
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="flex-1 rounded-lg border border-leadrs-stroke px-3 py-2 font-geist text-sm text-leadrs-text focus:border-leadrs-brand focus:outline-none"
                    placeholder="#000000"
                />
            </div>
        </div>
    );

    if (industriesLoading) {
        return (
            <div className="flex h-screen items-center justify-center">
                <p className="font-geist text-sm text-leadrs-text-soft">Chargement des industries...</p>
            </div>
        );
    }

    // Same field resolution as PublicLeadForm — single source of truth
    const _previewIndustry = industriesData.find(i => i.name === template.industry);
    const _previewMandatory: string[] = _previewIndustry?.mandatoryFields || [];
    const _previewOrder: string[] = template.fieldOrder || [];
    const previewFieldIds: string[] = [
        ..._previewOrder,
        ..._previewMandatory.filter(f => !_previewOrder.includes(f)),
    ];

    return (
        <div className="flex flex-col gap-6 pb-8">
            <div className="flex">
                <GoBack />
            </div>

            {/* Campaign Parameters */}
            <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                <div
                    className="flex cursor-pointer items-center justify-between"
                    onClick={() => setIsCampaignExpanded(!isCampaignExpanded)}
                >
                    <div>
                        <h2 className="font-geist text-base font-bold text-leadrs-text">
                            Paramètres de l'intégration API
                        </h2>
                        <p className="mt-1 font-geist text-sm text-leadrs-text-soft">
                            Configurez les critères de ciblage et les paramètres de votre intégration API
                        </p>
                    </div>
                    {isCampaignExpanded ? (
                        <MdExpandLess className="h-6 w-6 flex-shrink-0 text-leadrs-text" />
                    ) : (
                        <MdExpandMore className="h-6 w-6 flex-shrink-0 text-leadrs-text" />
                    )}
                </div>

                {isCampaignExpanded && (
                    <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div className="col-span-full">
                            <InputField
                                id="name"
                                label={isUniversal ? "Nom de l'iframe (optionnel)" : "Nom de l'intégration API"}
                                value={template.name}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('name', e.target.value)}
                                placeholder={isUniversal ? 'Iframe' : 'Mon intégration API'}
                            />
                        </div>

                        {isUniversal ? (
                            <div className="col-span-full rounded-lg border border-leadrs-stroke bg-leadrs-bg p-4">
                                <p className="mb-2 font-geist text-sm font-semibold text-leadrs-text">Comment ça marche</p>
                                <ol className="space-y-1.5 font-geist text-sm text-leadrs-text-soft">
                                    <li><span className="mr-1.5 font-semibold text-leadrs-brand">1.</span>Installez le Pixel Leadrs sur vos pages.</li>
                                    <li><span className="mr-1.5 font-semibold text-leadrs-brand">2.</span>Intégrez l'iframe et confirmez qu'elle est en ligne.</li>
                                    <li><span className="mr-1.5 font-semibold text-leadrs-brand">3.</span>Validez l'industrie détectée et fixez le prix en un clic.</li>
                                </ol>
                                <p className="mt-2 font-geist text-xs text-leadrs-text-soft">
                                    Ces trois étapes se suivent depuis l'onglet Mise en place de cette iframe.
                                </p>
                            </div>
                        ) : (
                        <>
                        <div className="col-span-full md:col-span-1">
                            <label className="ml-1 font-geist text-sm font-semibold text-leadrs-text">
                                Industrie
                            </label>
                            <div className="relative mt-2.5 w-full rounded-xl border border-leadrs-stroke px-2.5 py-3 focus-within:border-leadrs-brand">
                                <select
                                    id="industry"
                                    className="w-full cursor-pointer appearance-none bg-white pr-6 font-geist text-sm text-leadrs-text outline-none"
                                    value={template.industry || ''}
                                    onChange={(e) => handleIndustryChange(e.target.value)}
                                >
                                    <option value="">Selectionner une industrie</option>
                                    {industriesData.map((ind) => (
                                        <option key={ind._id} value={ind.name}>
                                            {ind.name}
                                        </option>
                                    ))}
                                </select>
                                <MdKeyboardArrowDown className="pointer-events-none absolute right-2.5 top-1/2 h-5 w-5 -translate-y-1/2 text-leadrs-text-soft" />
                            </div>
                        </div>

                        {template.industry && (
                            <div className="col-span-full md:col-span-1">
                                <label className="ml-1 font-geist text-sm font-semibold text-leadrs-text">
                                    Sous-industrie
                                </label>
                                <div className="relative mt-2.5 w-full rounded-xl border border-leadrs-stroke px-2.5 py-3 focus-within:border-leadrs-brand">
                                    <select
                                        id="subIndustry"
                                        className="w-full cursor-pointer appearance-none bg-white pr-6 font-geist text-sm text-leadrs-text outline-none"
                                        value={template.subIndustry || (() => {
                                            const selectedIndustry = industriesData.find(i => i.name === template.industry);
                                            return selectedIndustry && selectedIndustry.subIndustries.length > 0 ? selectedIndustry.subIndustries[0].name : '';
                                        })()}
                                        onChange={(e) => handleChange('subIndustry', e.target.value)}
                                    >
                                        {(() => {
                                            const selectedIndustry = industriesData.find(i => i.name === template.industry);
                                            return selectedIndustry?.subIndustries.map((item: any) => (
                                                <option key={item.name} value={item.name}>
                                                    {item.name}
                                                </option>
                                            ));
                                        })()}
                                    </select>
                                    <MdKeyboardArrowDown className="pointer-events-none absolute right-2.5 top-1/2 h-5 w-5 -translate-y-1/2 text-leadrs-text-soft" />
                                </div>
                            </div>
                        )}

                        <div className="col-span-full md:col-span-1">
                            <label className="ml-1 font-geist text-sm font-semibold text-leadrs-text">
                                Regions
                            </label>
                            <div className="mt-2.5 w-full">
                                <Select
                                    value={(template.regions || []).map((region: string) => ({
                                        value: region,
                                        label: region,
                                    }))}
                                    isMulti
                                    onChange={(items) => {
                                        const regions = items.map((item) => item.value);
                                        handleMultiChange({ regions });
                                        saveRegions(regions);
                                    }}
                                    options={frenchAreas.map((area) => ({
                                        value: area,
                                        label: area,
                                    }))}
                                    className="w-full font-geist"
                                    classNamePrefix="select"
                                    placeholder="Selectionner des regions"
                                    styles={regionsSelectStyles}
                                />
                            </div>
                        </div>

                        <div className="col-span-full md:col-span-1">
                            <label htmlFor="minimumPrice" className="ml-1 font-geist text-sm font-semibold text-leadrs-text">
                                Prix minimum auquel je souhaite vendre le lead
                            </label>
                            <div className="mt-2.5 w-full rounded-xl border border-leadrs-stroke px-2.5 py-3 focus-within:border-leadrs-brand">
                                <input
                                    id="minimumPrice"
                                    type="number"
                                    className="w-full bg-white font-geist text-sm text-leadrs-text outline-none placeholder:text-leadrs-text-soft"
                                    value={template.minimumPrice?.toString() || ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const value = e.target.value ? parseFloat(e.target.value) : undefined;
                                        handleChange('minimumPrice', value as any);
                                    }}
                                    placeholder="Ex: 15"
                                />
                            </div>
                        </div>
                        </>
                        )}
                    </div>
                )}
            </div>

            {/* Design Studio */}
            <div className="overflow-hidden rounded-xl border border-leadrs-stroke bg-white">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-leadrs-stroke px-6 py-5">
                    <div>
                        <h2 className="font-geist text-base font-bold text-leadrs-text">Design Studio</h2>
                        <p className="mt-0.5 font-geist text-sm text-leadrs-text-soft">
                            Sélectionnez un thème ou personnalisez chaque détail — l'aperçu se met à jour en temps réel.
                        </p>
                    </div>
                    <span className="flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1 font-geist text-xs font-semibold text-green-600">
                        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-green-500 inline-block" />
                        Aperçu en direct
                    </span>
                </div>

                {/* Preset gallery */}
                <div className="border-b border-leadrs-stroke px-6 py-4">
                    <p className="mb-3 font-geist text-[10px] font-semibold uppercase tracking-widest text-leadrs-text-soft">
                        Thèmes prédéfinis
                    </p>
                    <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
                        {DESIGN_PRESETS.map((preset) => (
                            <button
                                key={preset.id}
                                type="button"
                                onClick={() => applyPreset(preset)}
                                className={`group flex-shrink-0 rounded-xl border-2 p-3 text-left transition-all duration-200 hover:scale-[1.04] hover:shadow-md ${
                                    selectedPreset === preset.id
                                        ? 'border-leadrs-brand shadow-md ring-2 ring-leadrs-brand/20'
                                        : 'border-leadrs-stroke hover:border-leadrs-brand/50'
                                }`}
                                style={{ minWidth: 96 }}
                            >
                                <div
                                    className="mb-2.5 overflow-hidden rounded-lg"
                                    style={{
                                        ...(preset.preview.bg === 'transparent' ? CHECKERBOARD : { backgroundColor: preset.preview.bg }),
                                        border: `1.5px solid ${preset.preview.accent}40`,
                                        padding: '8px 7px',
                                    }}
                                >
                                    <div style={{ backgroundColor: `${preset.preview.text}18`, borderRadius: 3, height: 5, marginBottom: 5, width: '70%' }} />
                                    <div style={{ backgroundColor: `${preset.preview.text}0d`, borderRadius: 3, height: 13, marginBottom: 4, border: `1px solid ${preset.preview.text}18` }} />
                                    <div style={{ backgroundColor: `${preset.preview.text}0d`, borderRadius: 3, height: 13, marginBottom: 6, border: `1px solid ${preset.preview.text}18` }} />
                                    <div style={{ backgroundColor: preset.preview.accent, borderRadius: 3, height: 13 }} />
                                </div>
                                <p className="font-geist text-xs font-semibold text-leadrs-text leading-tight">{preset.name}</p>
                                <p className="mt-0.5 font-geist text-[10px] leading-tight text-leadrs-text-soft">{preset.description}</p>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Controls + Live Preview */}
                <div className="grid grid-cols-1 xl:grid-cols-2 divide-y xl:divide-y-0 xl:divide-x divide-leadrs-stroke">
                    {/* Left: Controls */}
                    <div className="p-6">
                        <div className="mb-5 flex gap-1 rounded-lg bg-leadrs-stroke p-1">
                            {(['container', 'text', 'inputs', 'button', 'order'] as const)
                                .filter((tab) => !(isUniversal && tab === 'order'))
                                .map((tab) => {
                                const labels: Record<string, string> = { container: 'Conteneur', text: 'Texte', inputs: 'Champs', button: 'Bouton', order: 'Ordre' };
                                return (
                                    <button
                                        key={tab}
                                        type="button"
                                        onClick={() => setDesignTab(tab)}
                                        className={`flex-1 rounded-md px-2 py-2 font-geist text-xs font-semibold transition-all ${
                                            designTab === tab
                                                ? 'bg-white text-leadrs-text shadow-sm'
                                                : 'text-leadrs-text-soft hover:text-leadrs-text'
                                        }`}
                                    >
                                        {labels[tab]}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="space-y-4">
                            {designTab === 'container' && (
                                <>
                                    <label className="flex cursor-pointer items-center gap-3 rounded-lg bg-leadrs-bg p-3">
                                        <input
                                            type="checkbox"
                                            checked={template.backgroundColor === 'transparent'}
                                            onChange={(e) => handleChange('backgroundColor', e.target.checked ? 'transparent' : '#ffffff')}
                                            className="h-4 w-4 cursor-pointer accent-leadrs-brand"
                                        />
                                        <span className="font-geist text-sm font-medium text-leadrs-text">
                                            Fond transparent
                                            <span className="ml-1.5 font-geist text-xs font-normal text-leadrs-text-soft">(idéal pour iframe)</span>
                                        </span>
                                    </label>
                                    {template.backgroundColor !== 'transparent' && renderColorInput('Couleur de fond', template.backgroundColor, (v) => handleChange('backgroundColor', v))}
                                    {renderColorInput('Couleur de bordure', template.borderColor, (v) => handleChange('borderColor', v))}
                                    <div className="grid grid-cols-2 gap-4">
                                        <InputField
                                            id="borderWidth"
                                            label="Épaisseur de bordure"
                                            value={template.borderWidth}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('borderWidth', e.target.value)}
                                            placeholder="1px"
                                        />
                                        <InputField
                                            id="borderRadius"
                                            label="Arrondi des bords"
                                            value={template.borderRadius}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('borderRadius', e.target.value)}
                                            placeholder="12px"
                                        />
                                    </div>
                                </>
                            )}
                            {designTab === 'text' && (
                                <>
                                    {renderColorInput('Couleur du titre', template.textColor, (v) => handleChange('textColor', v))}
                                    {renderColorInput('Couleur des labels', template.labelColor, (v) => handleChange('labelColor', v))}
                                    <InputField
                                        id="fontFamily"
                                        label="Police de caractères"
                                        value={template.fontFamily || 'inherit'}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('fontFamily', e.target.value)}
                                        placeholder="inherit"
                                    />
                                    <InputField
                                        id="fontSize"
                                        label="Taille de police"
                                        value={template.fontSize || '16px'}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('fontSize', e.target.value)}
                                        placeholder="16px"
                                    />
                                </>
                            )}
                            {designTab === 'inputs' && (
                                <>
                                    {renderColorInput('Fond des champs', template.inputBackgroundColor, (v) => handleChange('inputBackgroundColor', v))}
                                    {renderColorInput('Bordure des champs', template.inputBorderColor, (v) => handleChange('inputBorderColor', v))}
                                    {renderColorInput('Texte des champs', template.inputTextColor, (v) => handleChange('inputTextColor', v))}
                                </>
                            )}
                            {designTab === 'button' && (
                                <>
                                    {renderColorInput('Couleur de fond', template.buttonBackgroundColor, (v) => handleChange('buttonBackgroundColor', v))}
                                    {renderColorInput('Couleur du texte', template.buttonTextColor, (v) => handleChange('buttonTextColor', v))}
                                    <InputField
                                        id="ctaText"
                                        label="Texte du bouton CTA"
                                        value={template.ctaText || ''}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('ctaText', e.target.value)}
                                        placeholder="Envoyer ma demande"
                                    />
                                </>
                            )}
                            {designTab === 'order' && (() => {
                                const industry = industriesData.find(i => i.name === template.industry);
                                if (!industry) return (
                                    <p className="font-geist text-sm text-leadrs-text-soft">
                                        Sélectionnez une industrie pour voir les champs disponibles.
                                    </p>
                                );
                                const mandatorySet = new Set(industry.mandatoryFields || []);
                                const optionalFields = (industry.fields || []).filter(f => !mandatorySet.has(f));
                                const visibleFields = previewFieldIds;
                                const hiddenOptional = optionalFields.filter(f => !previewFieldIds.includes(f));
                                const typeLabel: Record<string, string> = { string: 'Texte', select: 'Liste', 'multi-select': 'Multi', number: 'Nombre' };
                                return (
                                    <div className="space-y-3">
                                        <div className="space-y-1.5">
                                            {visibleFields.map((fieldId, index) => {
                                                const fieldDef = formFields.find(f => f.id === fieldId);
                                                const isMandatory = mandatorySet.has(fieldId);
                                                return (
                                                    <div key={fieldId} className="flex items-center gap-2 rounded-lg border border-leadrs-stroke bg-white px-3 py-2">
                                                        <div className="flex flex-col gap-0.5 text-leadrs-text-soft">
                                                            <button type="button" onClick={() => moveFieldInOrder(fieldId, 'up')} disabled={index === 0} className="rounded p-0.5 transition-colors hover:bg-leadrs-bg disabled:opacity-20">
                                                                <svg width="10" height="6" viewBox="0 0 10 6" fill="none"><path d="M9 5L5 1 1 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                                            </button>
                                                            <button type="button" onClick={() => moveFieldInOrder(fieldId, 'down')} disabled={index === visibleFields.length - 1} className="rounded p-0.5 transition-colors hover:bg-leadrs-bg disabled:opacity-20">
                                                                <svg width="10" height="6" viewBox="0 0 10 6" fill="none"><path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                                            </button>
                                                        </div>
                                                        <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-leadrs-bg font-geist text-[10px] font-semibold text-leadrs-text-soft">{index + 1}</span>
                                                        <span className="flex-1 font-geist text-sm font-medium text-leadrs-text">{fieldDef?.name || fieldId}</span>
                                                        <span className="rounded-full bg-leadrs-bg px-2 py-0.5 font-geist text-[10px] text-leadrs-text-soft">{typeLabel[fieldDef?.type || 'string'] ?? 'Texte'}</span>
                                                        {isMandatory
                                                            ? <span title="Champ obligatoire" className="font-geist text-[10px] text-leadrs-text-soft">🔒</span>
                                                            : <button type="button" onClick={() => toggleOptionalField(fieldId)} className="rounded p-0.5 text-leadrs-text-soft transition-colors hover:text-red-500"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg></button>
                                                        }
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        {hiddenOptional.length > 0 && (
                                            <div className="space-y-1.5">
                                                <p className="font-geist text-[10px] font-semibold uppercase tracking-widest text-leadrs-text-soft">Champs optionnels</p>
                                                {hiddenOptional.map(fieldId => {
                                                    const fieldDef = formFields.find(f => f.id === fieldId);
                                                    return (
                                                        <div key={fieldId} className="flex items-center gap-2 rounded-lg border border-dashed border-leadrs-stroke bg-gray-50 px-3 py-2">
                                                            <span className="flex-1 font-geist text-sm text-leadrs-text">{fieldDef?.name || fieldId}</span>
                                                            <span className="rounded-full bg-leadrs-bg px-2 py-0.5 font-geist text-[10px] text-leadrs-text-soft">{typeLabel[fieldDef?.type || 'string'] ?? 'Texte'}</span>
                                                            <button type="button" onClick={() => toggleOptionalField(fieldId)} className="rounded p-0.5 text-leadrs-text-soft transition-colors hover:text-leadrs-brand">
                                                                <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M5 1v8M1 5h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>
                    </div>

                    {/* Right: Live Preview */}
                    <div className="flex flex-col bg-leadrs-bg p-6">
                        <p className="mb-3 font-geist text-[10px] font-semibold uppercase tracking-widest text-leadrs-text-soft">
                            Aperçu en direct
                        </p>
                        {isUniversal && (
                            <p className="mb-3 font-geist text-xs text-leadrs-text-soft">
                                Les champs du formulaire ne sont pas prévisualisables ici — ils sont déterminés page par page,
                                une fois l'industrie détectée par le Pixel.
                            </p>
                        )}

                        <div className="overflow-hidden rounded-xl border border-leadrs-stroke bg-white shadow-sm flex-1">
                            <div className="flex items-center gap-2 border-b border-leadrs-stroke bg-gray-50 px-4 py-2.5">
                                <div className="flex gap-1.5">
                                    <div className="h-2.5 w-2.5 rounded-full bg-red-400" />
                                    <div className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
                                    <div className="h-2.5 w-2.5 rounded-full bg-green-400" />
                                </div>
                                <div className="mx-2 flex-1 rounded-md border border-leadrs-stroke bg-white px-3 py-1">
                                    <p className="font-geist text-[10px] text-leadrs-text-soft">votre-site.com</p>
                                </div>
                            </div>

                            <div className="p-4" style={{ backgroundColor: '#f8fafc' }}>
                                <div
                                    style={{
                                        ...(template.backgroundColor === 'transparent' ? CHECKERBOARD : { backgroundColor: template.backgroundColor }),
                                        border: `${template.borderWidth} solid ${template.borderColor}`,
                                        borderRadius: template.borderRadius,
                                        fontFamily: template.fontFamily || 'inherit',
                                        padding: '20px',
                                        transition: 'all 0.25s ease',
                                    }}
                                >
                                    <div style={{ color: template.textColor, fontWeight: 700, fontSize: 15, marginBottom: 4, transition: 'color 0.2s' }}>
                                        Obtenez votre devis gratuit
                                    </div>
                                    <div style={{ color: template.labelColor, fontSize: 12, marginBottom: 18, opacity: 0.7, transition: 'color 0.2s' }}>
                                        Remplissez le formulaire ci-dessous
                                    </div>
                                    {previewFieldIds.map((fieldId: string) => {
                                            const fieldDef = formFields.find(f => f.id === fieldId);
                                            const label = fieldDef?.name || fieldId;
                                            const fieldType = fieldDef?.type || 'string';
                                            const options = fieldDef?.options || [];
                                            const useButtonGrid = fieldType === 'select' && options.length <= 4;
                                            return (
                                                <div key={fieldId} style={{ marginBottom: 13 }}>
                                                    <div style={{ color: template.labelColor, fontSize: 11, fontWeight: 500, marginBottom: 5 }}>
                                                        {label}
                                                    </div>
                                                    {useButtonGrid ? (
                                                        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(options.length, 2)}, 1fr)`, gap: 4 }}>
                                                            {options.map((opt) => (
                                                                <div key={opt} style={{ backgroundColor: template.inputBackgroundColor, border: `1.5px solid ${template.inputBorderColor}`, borderRadius: 8, padding: '6px 8px', fontSize: 10, color: template.inputTextColor, textAlign: 'center', opacity: 0.7 }}>
                                                                    {opt}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : fieldType === 'select' ? (
                                                        <div style={{ backgroundColor: template.inputBackgroundColor, border: `1px solid ${template.inputBorderColor}`, borderRadius: 6, padding: '7px 11px', color: template.inputTextColor, fontSize: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <span style={{ opacity: 0.55 }}>{options[0] ?? 'Sélectionner...'}</span>
                                                            <svg width="10" height="6" viewBox="0 0 10 6" fill="none" style={{ opacity: 0.4 }}><path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                                        </div>
                                                    ) : fieldType === 'multi-select' ? (
                                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 4 }}>
                                                            {options.map((opt) => (
                                                                <div key={opt} style={{ backgroundColor: template.inputBackgroundColor, border: `1.5px solid ${template.inputBorderColor}`, borderRadius: 8, padding: '6px 8px', fontSize: 10, color: template.inputTextColor, display: 'flex', alignItems: 'center', gap: 5, opacity: 0.7 }}>
                                                                    <div style={{ width: 10, height: 10, border: `1.5px solid ${template.inputBorderColor}`, borderRadius: 2, flexShrink: 0 }} />
                                                                    {opt}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <div style={{ backgroundColor: template.inputBackgroundColor, border: `1px solid ${template.inputBorderColor}`, borderRadius: 6, padding: '7px 11px', color: template.inputTextColor, fontSize: 12, opacity: 0.55 }}>
                                                            {FIELD_PLACEHOLDERS[label] ?? label}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    <div
                                        style={{
                                            backgroundColor: template.buttonBackgroundColor,
                                            color: template.buttonTextColor,
                                            borderRadius: 6,
                                            padding: '10px 0',
                                            textAlign: 'center',
                                            fontWeight: 600,
                                            fontSize: 12,
                                            marginTop: 6,
                                            transition: 'all 0.2s',
                                        }}
                                    >
                                        {template.ctaText || 'Envoyer ma demande'}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-3 flex flex-wrap gap-2.5">
                            {[
                                { color: template.backgroundColor, label: 'Fond' },
                                { color: template.buttonBackgroundColor, label: 'Bouton' },
                                { color: template.textColor, label: 'Texte' },
                                { color: template.inputBorderColor, label: 'Champs' },
                            ].map(({ color, label }) => (
                                <div key={label} className="flex items-center gap-1.5">
                                    <div
                                        className="h-3.5 w-3.5 rounded-full border border-leadrs-stroke shadow-sm flex-shrink-0"
                                        style={color === 'transparent' ? CHECKERBOARD : { backgroundColor: color }}
                                    />
                                    <span className="font-geist text-[10px] text-leadrs-text-soft">{label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
                <button
                    onClick={() => navigate('/integrations')}
                    disabled={isLoading}
                    className="flex items-center gap-2 rounded-lg border border-leadrs-stroke px-5 py-2 font-geist text-sm font-medium text-leadrs-text-soft transition-colors hover:border-leadrs-brand disabled:opacity-50"
                >
                    <MdClose className="h-5 w-5" />
                    Annuler
                </button>
                <button
                    onClick={handleCreate}
                    disabled={isLoading}
                    className="flex items-center gap-2 rounded-lg bg-leadrs-brand px-5 py-2 font-geist text-sm font-medium text-white transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    <MdSave className="h-5 w-5" />
                    {isLoading ? 'Création…' : "Créer l'intégration API"}
                </button>
            </div>
        </div>
    );
};

export default NewFormTemplate;
